import type { ContractRequest } from '@/types/contractRequests'
import type { Contract } from '@/types/contracts'
import type { BelongingCovers } from '@/types/covers'

export interface ContractRef {
  contractNumber: string
  productLine?: { [key: string]: string } | null
}

export interface BrokerChangeSummary {
  insurerName: string
  contracts: ContractRef[]
  status: DocumentStatuses
}

export interface Document {
  date: { fr: string; nl: string }
  dateSortKey: string
  title: { [key: string]: string }
  subtitle: { [key: string]: string }
  status: string
  url: string
  type: DocumentType
  id: string
  source?: string
  receiveTime?: string
  subjectId?: string
  productType?: string
  fileId?: string
  subjectType?: string
  examples?: {
    title: { [key: string]: string }
    description: { [key: string]: string }
    url: string
  }[]
  mimeTypes: string[]
  newSignature: boolean
  contractRequestUuid: string
  contract: Contract | ContractRequest
  temporary?: boolean
  additionalDetails: {
    covers?: BelongingCovers[]
    startDate?: string
    description?: string
    enforcedAt?: string
    oldInsurerName?: string
    newInsurerName?: string
    policyEndDate?: string
    policyNumber?: string
    insurerName?: string
    contracts?: ContractRef[]
    documents?: EnvelopeDocument[]
  }
}

export interface EnvelopeDocument {
  id: string
  type: DocumentType
  url: string
  details: EnvelopeDocumentDetails
}

export interface EnvelopeDocumentDetails {
  uuid?: string
  status?: DocumentStatuses
  reference?: string
  productType?: string
  productShortLabel?: { [key: string]: string }
  insurer?: Contract['insurer']
  contractType?: string
  pendingSignature?: boolean
  payment?: {
    premium: number
  } | null
  startDate?: { [key: string]: string } | null
  covers?: BelongingCovers[]
  oldInsurerName?: string | null
  newInsurerName?: string | null
  policyEndDate?: string | null
  policyNumber?: string | null
  insurerName?: string | null
  productLine?: { [key: string]: string } | null
  contracts?: ContractRef[]
  description?: string
  enforcedAt?: string
  number?: string
  contract?: Contract
}

export interface SupportingDocument {
  attachedAt: Date
  contractId: string
  documentType: SupportingDocumentType
  documentUrl: string
  id: string
  comment: string | null
}

export enum SupportingDocumentType {
  AffiliateList = 'affiliate_list',
  AmlFile = 'aml_file',
  AnnualLifeInsuranceStatement = 'annual_life_insurance_statement',
  BeneficiaryIdCard = 'beneficiary_id_card',
  ClaimHistoryStatement = 'claim_history_statement',
  ClientInformationSheet = 'client_information_sheet',
  CompanyDirectorsList = 'company_directors_list',
  CoverageCertificate = 'coverage_certificate',
  DebtCertificate = 'debt_certificate',
  DriverLicense = 'driver_license',
  Email = 'email',
  FleetVehicleList = 'fleet_vehicle_list',
  IpidDocument = 'ipid_document',
  LeaseAgreement = 'lease_agreement',
  LeasingContract = 'leasing_contract',
  LicensePlateCancellationProof = 'license_plate_cancellation_proof',
  MyCareerRecord = 'my_career_record',
  MyPensionRecord = 'my_pension_record',
  OdometerPhoto = 'odometer_photo',
  PensionCertificate = 'pension_certificate',
  PolicyTerminationProof = 'policy_termination_proof',
  PreContractualDisclosure = 'pre_contractual_disclosure',
  PremiumPaymentProof = 'premium_payment_proof',
  PropertyPhotos = 'property_photos',
  PurchaseInvoice = 'purchase_invoice',
  RegistrationCard = 'registration_card',
  RegistrationRequest = 'registration_request',
  SupplementaryPensionStatement = 'supplementary_pension_statement',
  TaxAssessmentNotice = 'tax_assessment_notice',
  TaxCertificate = 'tax_certificate',
  TermsAndConditions = 'terms_and_conditions',
  VehiclePhoto = 'vehicle_photo',
  VehicleRegistrationCertificate = 'vehicle_registration_certificate'
}

export enum DocumentType {
  Amendment = 'amendment',
  Endorsement = 'endorsement',
  Contract = 'contract',
  Termination = 'termination',
  Cancellation = 'cancellation',
  SepaMandate = 'sepa_mandate',
  Transfer = 'transfer',
  BrokerChange = 'broker_change',
  LifeProcedure = 'life_procedure',
  SignatureEnvelope = 'signature_envelope',
  GreenCard = 'green_card',
  DrivingLicence = 'DrivingLicence',
  AccidentRecord = 'Documents::AccidentRecord'
}

export enum InsuranceCertificateSource {
  Insurer = 'insurer',
  Operator = 'operator'
}

export interface InsuranceCertificate {
  date: { fr: string; nl: string }
  title: { [key: string]: string }
  subtitle: { [key: string]: string }
  status: string
  url: string
  type: string
  temporary: boolean
  source: InsuranceCertificateSource
  receiveTime: string
}

export interface UserDocument {
  uuid: string
  contractRequest: ContractRequest
  documents: Document[]
}

export interface DocumentData {
  file: any // to resolve (File not a type)
  subjectId: string
  documentableType: string
  subjectType: string
  contractRequestUuid: string
}

export interface DeleteDocumentData {
  documentUuid: string
  fileId: string
}

export interface DrivingLicenceDocumentData {
  personUuid: string
  uuid: string
  temporay: boolean
}

export interface DocumentCreated {
  error: any
  data: any
}

export const toSignStatuses = ['waiting_for_signature', 'waiting_for_completion', 'waiting_for_contract']

export enum DocumentStatuses {
  Processed = 'processed',
  Accepted = 'accepted',
  Sent = 'sent',
  Rejected = 'rejected',
  WaitingForSignature = 'waiting_for_signature',
  WaitingForCompletion = 'waiting_for_completion',
  WaitingForContract = 'waiting_for_contract',
  Signed = 'signed',
  Archived = 'archived',
  Terminated = 'terminated',
  RequestSubmitted = 'request_submitted',
  Completed = 'completed'
}

export const signedTerminationStatuses = [DocumentStatuses.Processed, DocumentStatuses.Accepted, DocumentStatuses.Sent]
export const signedEndorsementStatuses = [
  DocumentStatuses.Processed,
  DocumentStatuses.Archived,
  DocumentStatuses.Terminated
]

export const inProgressBrokerChangesStatuses = [
  DocumentStatuses.WaitingForSignature,
  DocumentStatuses.Signed,
  DocumentStatuses.Sent,
  DocumentStatuses.RequestSubmitted
]

export const signedBrokerChangesStatuses = [
  DocumentStatuses.Processed,
  DocumentStatuses.Completed,
  DocumentStatuses.RequestSubmitted
]
